import { Injectable } from '@angular/core';
import {
  demoMemberId,
  demoPerspective,
  demoPersona,
  demoSettings,
  demoToday,
  storyDay,
  updateDemoSettings,
} from '@troyai/portal/investor-demo/core';

import { DEMO_DATA_KEY, DemoData, seedDemoData } from './demo-data';

/** The collections every reader walks; a stored object missing one cannot be served. */
const LIST_KEYS = [
  'storyEntries',
  'episodes',
  'assessments',
  'suggestions',
  'carePlans',
  'caseNotes',
] as const;

/**
 * A saved state is served only when it is of this version, for this story day (its dates were
 * written for that day), for this member, for this perspective and for this persona; anything
 * else is reseeded.
 */
function isUsable(value: unknown): value is DemoData {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const raw = value as Record<string, unknown>;
  return (
    raw['version'] === 5 &&
    raw['perspective'] === demoPerspective() &&
    raw['personaEmail'] === demoPersona().email &&
    raw['storyDay'] === storyDay() &&
    raw['memberId'] === demoMemberId() &&
    typeof raw['nextId'] === 'number' &&
    typeof raw['patient'] === 'object' &&
    raw['patient'] !== null &&
    typeof raw['careSignals'] === 'object' &&
    raw['careSignals'] !== null &&
    LIST_KEYS.every((key) => Array.isArray(raw[key]))
  );
}

/**
 * The fake backend's data: loaded once from localStorage (else the seed), saved after every
 * change. Missing, unparseable, old-version, other-day, other-member, other-perspective or
 * other-persona storage, or storage that throws, falls back to the seed, and a story reseeded on
 * load starts before the "+3 days" jump. Never throws.
 */
@Injectable({ providedIn: 'root' })
export class DemoStore {
  private current: DemoData | null = null;

  /**
   * The live data object. Change it only through `mutate`, so the change is saved. When the
   * perspective or the persona changed since it was loaded (the real `me` answered after the first
   * read), the story starts over.
   */
  data(): DemoData {
    if (!this.current) {
      this.current = this.load();
    } else if (
      this.current.perspective !== demoPerspective() ||
      this.current.personaEmail !== demoPersona().email
    ) {
      this.current = this.freshStory();
    }
    return this.current;
  }

  /** Applies `fn` to the data, saves, and returns what `fn` returned. A failed save is ignored. */
  mutate<T>(fn: (data: DemoData) => T): T {
    const data = this.data();
    const result = fn(data);
    this.save(data);
    return result;
  }

  /** Back to the seed, saved. */
  reset(): void {
    this.current = seedDemoData();
    this.save(this.current);
  }

  /** The items whose `visibleFrom` has arrived on the demo clock, in their order. */
  visible<T extends { visibleFrom?: string }>(items: T[]): T[] {
    const today = demoToday();
    return items.filter((item) => !item.visibleFrom || item.visibleFrom <= today);
  }

  private load(): DemoData {
    try {
      const text = localStorage.getItem(DEMO_DATA_KEY);
      if (text) {
        const parsed: unknown = JSON.parse(text);
        if (isUsable(parsed)) {
          return parsed;
        }
      }
    } catch {
      // Unparseable data or storage that throws: start from the seed.
    }
    return this.freshStory();
  }

  /**
   * The seed, for a story that starts over. The "+3 days" jump lives in the demo settings and
   * outlives the saved data, so it is cleared here: a new story begins before the jump.
   */
  private freshStory(): DemoData {
    if (demoSettings().jumped) {
      updateDemoSettings({ jumped: false });
    }
    return seedDemoData();
  }

  private save(data: DemoData): void {
    try {
      localStorage.setItem(DEMO_DATA_KEY, JSON.stringify(data));
    } catch {
      // Storage missing or full: the in-memory copy still serves this page load.
    }
  }
}
